import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
  X, Check, ChevronDown, AlertTriangle, CheckCircle2, Info, XCircle,
  Loader2, Search, Inbox,
} from 'lucide-react';
import { useUI } from '../lib/store';
import { detailText } from '@/stores/ui';
import { Select as FieldSelect } from '@/components/ui/select';
import { DateField } from '@/components/ui/date-field';
import { TimeField } from '@/components/ui/time-field';

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-soft';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ElementType;
  loading?: boolean;
  iconOnly?: boolean;
};

export function Button({
  variant = 'secondary', size = 'md', icon: Icon, loading, iconOnly,
  className, children, disabled, ...rest
}: ButtonProps) {
  const variantClass = {
    primary: 'btn-primary', secondary: 'btn-secondary', ghost: 'btn-ghost',
    danger: 'btn-danger', 'danger-soft': 'btn-danger-soft',
  }[variant];
  return (
    <button
      className={clsx(
        variantClass,
        size === 'sm' && 'btn-sm', size === 'lg' && 'btn-lg',
        iconOnly && 'btn-icon', className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Loader2 size={14} className="animate-spin" />
        : Icon ? <Icon size={size === 'sm' ? 13 : 15} /> : null}
      {!iconOnly && children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------
export function Field({
  label, required, error, hint, className, children, htmlFor,
}: {
  // A hint can carry a link to the screen that fills an empty list, so it
  // takes anything renderable rather than only a string.
  label?: string; required?: boolean; error?: string; hint?: React.ReactNode;
  className?: string; children: React.ReactNode; htmlFor?: string;
}) {
  return (
    <div className={className}>
      {label && (
        <label className="label" htmlFor={htmlFor}>
          {label}
          {required && <span className="text-critical-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error ? <p className="field-error">{error}</p>
        : hint ? <p className="text-2xs text-ink-400 mt-1">{hint}</p> : null}
    </div>
  );
}

/**
 * Scroll to the first field that is wrong and put the cursor in it.
 *
 * Called after a form's own checks have failed. Errors are only ever
 * rendered by `Field` above, and only for a form that is open, so the first
 * `.field-error` on the page is the first problem the reader has.
 */
export function showFirstProblem() {
  requestAnimationFrame(() => {
    const note = document.querySelector('.field-error');
    if (!note) return;
    note.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const control = note.parentElement?.querySelector<HTMLElement>('input, select, textarea, [tabindex]');
    control?.focus({ preventScroll: true });
  });
}
export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { error?: boolean }>(
  ({ className, error, ...rest }, ref) =>
    // Same reasoning as the dropdown below: a date drawn by the browser is
    // the one control on the form that does not belong to this product.
    (rest.type === 'date'
      ? <DateField ref={ref} className={className} error={error} {...rest} />
      : rest.type === 'time'
        ? <TimeField ref={ref} className={className} error={error} {...rest} />
        : <input ref={ref} className={clsx('input', error && 'input-error', className)} {...rest} />),
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, rows = 3, ...rest }, ref) => (
    <textarea ref={ref} rows={rows} className={clsx('input', className)} {...rest} />
  ),
);
Textarea.displayName = 'Textarea';

/**
 * A dropdown, in this product's own clothes.
 *
 * This rendered a bare <select>, so four hundred and one dropdowns across
 * the app were drawn by the browser: Chrome's list on Windows, a wheel on a
 * phone, something else again on a Mac. Beside fields the product had styled
 * itself, they were the one control that plainly belonged to someone else —
 * different height, different radius, different type, and no way to search a
 * list of four hundred shops.
 *
 * The proper one already existed; HR had been using it. It renders into a
 * portal (so a dropdown inside a table or a card is never clipped), searches
 * once the list is longer than six, and marks what is chosen.
 *
 * So this is now a translation rather than a control: the `options` prop
 * becomes <option> children and that component does the work. Callers are
 * untouched — the one underneath keeps a hidden native <select> in step, so
 * `onChange(e) => e.target.value` still reads exactly as it always did, and
 * a form that submits normally still submits.
 */
export function Select({
  options, placeholder, className, error, ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement> & {
  options: { value: any; label: string; disabled?: boolean }[];
  placeholder?: string; error?: boolean;
}) {
  return (
    <FieldSelect className={className} error={error} {...rest}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={String(o.value)} value={o.value} disabled={o.disabled}>{o.label}</option>
      ))}
    </FieldSelect>
  );
}

export function Checkbox({
  label, checked, onChange, disabled, description,
}: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
  disabled?: boolean; description?: string;
}) {
  return (
    <label className={clsx('flex items-start gap-2.5 cursor-pointer select-none group',
      disabled && 'opacity-50 pointer-events-none')}>
      <span
        className={clsx(
          'mt-0.5 w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center shrink-0 transition-colors',
          checked ? 'bg-brand-600 border-brand-600' : 'bg-white border-ink-300 group-hover:border-ink-400',
        )}
      >
        {checked && <Check size={12} className="text-white" strokeWidth={3} />}
      </span>
      <input type="checkbox" className="sr-only" checked={checked} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)} />
      <span className="min-w-0">
        <span className="text-[15px] text-ink-700 dark:text-ink-300">{label}</span>
        {description && <span className="block text-xs text-ink-500 mt-0.5">{description}</span>}
      </span>
    </label>
  );
}

export function Toggle({
  checked, onChange, label, disabled,
}: { checked: boolean; onChange: (v: boolean) => void; label?: string; disabled?: boolean }) {
  return (
    /*
     * shrink-0 on the whole control, not only on the track.
     *
     * A filter bar is `flex flex-wrap`, so everything in it is a flex item
     * that may be squeezed. The track said shrink-0 and the label did not,
     * which meant every pixel the row was short came out of the words: on a
     * narrow bar or at browser zoom the text was crushed back under the pill
     * and "Inactive" read as "nactive". It happened on all fifty-nine of
     * these, because it is the component and not the screen.
     */
    <label className={clsx('inline-flex shrink-0 items-center gap-2.5 cursor-pointer select-none',
      disabled && 'opacity-50 pointer-events-none')}>
      <button
        type="button" role="switch" aria-checked={checked} disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative w-9 h-5 rounded-full transition-colors shrink-0',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
          checked ? 'bg-brand-600' : 'bg-ink-300 dark:bg-ink-700',
        )}
      >
        {/* Anchored left and moved by a whole knob's width, so where it sits
            never depends on the static position of an absolute child. */}
        <span className={clsx(
          'absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-4' : 'translate-x-0',
        )} />
      </button>
      {label && <span className="whitespace-nowrap text-[15px] text-ink-700 dark:text-ink-300">{label}</span>}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------
export function Card({
  title, subtitle, actions, footer, className, bodyClass, children, noPad,
}: {
  title?: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode;
  footer?: React.ReactNode; className?: string; bodyClass?: string;
  children: React.ReactNode; noPad?: boolean;
}) {
  return (
    <section className={clsx('card flex flex-col', className)}>
      {(title || actions) && (
        <header className="flex items-start justify-between gap-3 px-4 sm:px-5 pt-4 pb-3">
          <div className="min-w-0">
            {title && <h2 className="panel-title">{title}</h2>}
            {subtitle && <p className="text-xs muted mt-0.5">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </header>
      )}
      <div className={clsx('flex-1 min-h-0', !noPad && 'px-4 sm:px-5 pb-4 sm:pb-5', !title && !noPad && 'pt-4', bodyClass)}>
        {children}
      </div>
      {footer && (
        <footer className="px-4 sm:px-5 py-3 border-t border-ink-200 dark:border-ink-800 bg-ink-50/60 dark:bg-ink-900/60 rounded-b-xl">
          {footer}
        </footer>
      )}
    </section>
  );
}

export function Badge({
  tone = 'neutral', children, className,
}: { tone?: string; children: React.ReactNode; className?: string }) {
  const cls = {
    neutral: 'badge-neutral', brand: 'badge-brand', positive: 'badge-positive',
    caution: 'badge-caution', critical: 'badge-critical',
  }[tone] || 'badge-neutral';
  return <span className={clsx(cls, className)}>{children}</span>;
}

export function EmptyState({
  icon: Icon = Inbox, title, message, action,
}: { icon?: React.ElementType; title: string; message?: string; action?: React.ReactNode }) {
  return (
    // An empty list is the moment someone most needs telling what the screen
    // is for. The heading is sized to be read, not to apologise.
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      <div className="w-11 h-11 rounded-full bg-white dark:bg-ink-800 shadow-sm ring-1 ring-ink-200 dark:ring-ink-700 flex items-center justify-center mb-3">
        <Icon size={20} className="text-ink-400" />
      </div>
      <h3 className="text-[15px] font-semibold text-ink-800 dark:text-ink-200">{title}</h3>
      {message && <p className="text-[15px] muted mt-1 max-w-sm">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ label, className }: { label?: string; className?: string }) {
  return (
    <div className={clsx('flex items-center justify-center gap-2 py-10 text-ink-400', className)}>
      <Loader2 size={16} className="animate-spin" />
      {label && <span className="text-[15px]">{label}</span>}
    </div>
  );
}

export function SkeletonRows({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="p-3 space-y-2">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="skeleton h-6 flex-1" style={{ maxWidth: c === 0 ? 60 : undefined }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------
/*
 * A screen with nine tabs across the top asks the reader to choose before
 * they have seen anything. A tab marked `more` is kept out of the way until
 * someone asks for it, so what is on show is the handful of things people
 * come for. Nothing is taken away.
 */
export function Tabs({
  tabs, active, onChange, className,
}: {
  tabs: { key: string; label: string; count?: number; icon?: React.ElementType; more?: boolean }[];
  active: string; onChange: (k: string) => void; className?: string;
}) {
  const [showMore, setShowMore] = useState(false);
  const extras = tabs.filter((t) => t.more);
  const openedOnExtra = extras.some((t) => t.key === active);
  const visible = extras.length && !showMore && !openedOnExtra ? tabs.filter((t) => !t.more) : tabs;

  return (
    /*
     * A segmented control rather than an underline.
     *
     * An underline marks the tab you are on with two pixels at the bottom of
     * a word. On a screen with a toolbar under it, that line is read as the
     * edge of the toolbar and the tabs look like plain text — people did not
     * see they could be clicked. The raised pill on a sunken track says
     * "these are the choices, and you are on this one" without being read.
     */
    <div className={clsx(
      'inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-lg bg-ink-100/70 p-1 dark:bg-ink-800/50',
      className,
    )}>
      {visible.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={clsx(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[15px] font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'bg-card text-ink-900 shadow-[0_1px_2px_rgba(16,24,40,0.06)] dark:bg-ink-900 dark:text-ink-50'
                : 'text-ink-500 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-200',
            )}
          >
            {t.icon && <t.icon size={14} />}
            {t.label}
            {t.count !== undefined && (
              <span className={clsx('px-1.5 py-0.5 rounded text-[13px] tabular',
                isActive ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300' : 'bg-ink-200/70 text-ink-500 dark:bg-ink-800')}>
                {t.count}
              </span>
            )}
          </button>
        );
      })}
      {visible.length < tabs.length && (
        <button
          onClick={() => setShowMore(true)}
          className="flex items-center gap-1 rounded-md px-3 py-1.5 text-[15px] font-medium whitespace-nowrap text-ink-500 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-200"
        >
          More
          <ChevronDown size={14} />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal & Drawer
// ---------------------------------------------------------------------------
export function Modal({
  open, onClose, title, subtitle, children, footer, size = 'md', closeOnBackdrop = true,
}: {
  open: boolean; onClose: () => void; title?: React.ReactNode; subtitle?: React.ReactNode;
  children: React.ReactNode; footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'; closeOnBackdrop?: boolean;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const downOnBackdrop = useRef(false);

  if (!open) return null;
  const width = {
    sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl', full: 'max-w-[95vw]',
  }[size];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto">
      <div className="fixed inset-0 bg-ink-950/45 backdrop-blur-[2px] animate-fade-in"
        onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
        onClick={(e) => {
          // Only a press that both began and ended out here is a dismissal:
          // selecting text in a field and releasing past its edge is not.
          if (closeOnBackdrop && downOnBackdrop.current && e.target === e.currentTarget) onClose();
          downOnBackdrop.current = false;
        }} />
      <div className={clsx(
        'relative w-full my-auto bg-white dark:bg-ink-900 rounded-xl shadow-pop animate-scale-in flex flex-col max-h-[92vh]',
        width,
      )}>
        {(title || subtitle) && (
          <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-ink-200 dark:border-ink-800">
            <div className="min-w-0">
              <h2 className="text-[17px] font-semibold text-ink-900 dark:text-ink-50">{title}</h2>
              {subtitle && <p className="text-xs muted mt-0.5">{subtitle}</p>}
            </div>
            <button onClick={onClose} className="btn-ghost btn-icon btn-sm shrink-0" aria-label="Close">
              <X size={16} />
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-ink-200 dark:border-ink-800 bg-ink-50/70 dark:bg-ink-950/40 rounded-b-xl">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({
  open, onClose, title, subtitle, children, footer, width = 'max-w-2xl',
}: {
  open: boolean; onClose: () => void; title?: React.ReactNode; subtitle?: React.ReactNode;
  children: React.ReactNode; footer?: React.ReactNode; width?: string;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-ink-950/45 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />
      <aside className={clsx('relative w-full bg-white dark:bg-ink-900 shadow-pop flex flex-col animate-slide-up', width)}>
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-ink-200 dark:border-ink-800">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">{title}</h2>
            {subtitle && <p className="text-xs muted mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="btn-ghost btn-icon btn-sm" aria-label="Close"><X size={16} /></button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-ink-200 dark:border-ink-800">
            {footer}
          </footer>
        )}
      </aside>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Confirm dialog — used for every destructive action
// ---------------------------------------------------------------------------
export function ConfirmDialog({
  open, onClose, onConfirm, title, message, confirmLabel = 'Confirm',
  tone = 'danger', requireReason, reasonLabel = 'Reason', busy,
}: {
  open: boolean; onClose: () => void; onConfirm: (reason?: string) => void;
  title: string; message?: React.ReactNode; confirmLabel?: string;
  tone?: 'danger' | 'primary'; requireReason?: boolean; reasonLabel?: string; busy?: boolean;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  const blocked = requireReason && !reason.trim();

  return (
    <Modal
      open={open} onClose={onClose} title={title} size="sm"
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => onConfirm(reason.trim() || undefined)}
            disabled={blocked} loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      )}
    >
      <div className="flex gap-3">
        <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
          tone === 'danger' ? 'bg-critical-50 text-critical-600' : 'bg-brand-50 text-brand-600')}>
          <AlertTriangle size={18} />
        </div>
        <div className="min-w-0 flex-1">
          {message && <div className="text-[15px] text-ink-600 dark:text-ink-300 leading-relaxed">{message}</div>}
          {requireReason && (
            <Field label={reasonLabel} required className="mt-3">
              <Textarea
                value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                placeholder="This will be recorded in the audit trail"
                autoFocus
              />
            </Field>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Imperative confirm, so a handler can `await confirm({...})`. */
const ConfirmCtx = createContext<(o: any) => Promise<string | undefined | false>>(async () => false);
export const useConfirm = () => useContext(ConfirmCtx);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<any>(null);
  const resolver = useRef<(v: any) => void>();

  const confirm = (opts: any) => new Promise<string | undefined | false>((resolve) => {
    resolver.current = resolve;
    setState(opts);
  });

  const close = (value: any) => {
    resolver.current?.(value);
    setState(null);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={!!state}
        onClose={() => close(false)}
        onConfirm={(reason) => close(reason ?? '')}
        title={state?.title || 'Are you sure?'}
        message={state?.message}
        confirmLabel={state?.confirmLabel}
        tone={state?.tone}
        requireReason={state?.requireReason}
        reasonLabel={state?.reasonLabel}
      />
    </ConfirmCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------
export function Toaster() {
  const { toasts, dismiss } = useUI();
  const icons = { info: Info, success: CheckCircle2, warning: AlertTriangle, error: XCircle };
  const tones = {
    info: 'border-brand-200 bg-white text-brand-600',
    success: 'border-positive-200 bg-white text-positive-600',
    warning: 'border-caution-200 bg-white text-caution-600',
    error: 'border-critical-200 bg-white text-critical-600',
  };

  return createPortal(
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
      {toasts.map((t) => {
        const Icon = icons[t.tone];
        return (
          <div key={t.id}
            className={clsx(
              'pointer-events-auto flex gap-3 p-3.5 rounded-xl border shadow-float animate-slide-up dark:bg-ink-900',
              tones[t.tone],
            )}>
            <Icon size={17} className="shrink-0 mt-px" />
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-medium text-ink-900 dark:text-ink-100 leading-snug">{typeof t.title === 'string' ? t.title : detailText(t.title)}</p>
              {t.message && <p className="text-xs text-ink-600 dark:text-ink-400 mt-0.5">{t.message}</p>}
              {t.details?.length ? (
                <ul className="text-xs text-ink-600 dark:text-ink-400 mt-1.5 space-y-0.5 list-disc list-inside">
                  {t.details.map((d, i) => <li key={i}>{typeof d === 'string' ? d : detailText(d)}</li>)}
                </ul>
              ) : null}
              {/* The way back belongs next to the thing that happened. By the
                  time somebody has gone looking for the bin, they are no
                  longer sure what the shop was called. */}
              {t.action && (
                <button
                  type="button"
                  onClick={async () => { dismiss(t.id); await t.action!.run(); }}
                  className="mt-1.5 text-xs font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-800 dark:text-brand-300"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button onClick={() => dismiss(t.id)} className="shrink-0 text-ink-400 hover:text-ink-700 h-fit">
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
export function SearchInput({
  value, onChange, placeholder = 'Search…', className, autoFocus,
}: {
  value: string; onChange: (v: string) => void; placeholder?: string;
  className?: string; autoFocus?: boolean;
}) {
  return (
    <div className={clsx('relative', className)}>
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
      <input
        className="input pl-8 pr-8" value={value} placeholder={placeholder} autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
          <X size={13} />
        </button>
      )}
    </div>
  );
}

export function StatTile({
  label, value, sub, tone = 'neutral', icon: Icon, onClick, loading,
}: {
  label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string;
  icon?: React.ElementType; onClick?: () => void; loading?: boolean;
}) {
  /*
   * The figure is always near-black. The tone colours the mark, and the line
   * underneath if it carries one.
   *
   * It used to colour the figure itself, and a row of four tiles then showed
   * a blue number, a green number, an amber number and a red number — which
   * reads as one block of coloured figures rather than four facts. Worse, it
   * spent the strongest signal on the screen on something that is already the
   * biggest thing in the card: the number does not need colour to be found.
   * The small square does, because it is how the eye tells the tiles apart
   * before reading any of them.
   */
  const accent = 'text-ink-900 dark:text-ink-50';

  const chip = {
    positive: 'bg-positive-50 text-positive-600 dark:bg-positive-500/10 dark:text-positive-400',
    caution: 'bg-caution-50 text-caution-600 dark:bg-caution-500/10 dark:text-caution-400',
    critical: 'bg-critical-50 text-critical-600 dark:bg-critical-500/10 dark:text-critical-400',
    brand: 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300',
    neutral: 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400',
  }[tone] || 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400';

  return (
    <button
      type="button" onClick={onClick} disabled={!onClick}
      className={clsx(
        'card card-pad text-left w-full transition-all',
        onClick && 'hover:shadow-raised hover:border-ink-300 dark:hover:border-ink-700 cursor-pointer active:scale-[.995]',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="truncate text-[14px] font-medium text-ink-600 dark:text-ink-400">{label}</p>
        {/* The mark goes top-right, out of the reading line. In front of the
            label it was the first thing the eye met on a row of four tiles,
            and the label is what tells them apart. */}
        {Icon && (
          <span className={clsx('grid h-8 w-8 shrink-0 place-items-center rounded-xl', chip)}>
            <Icon size={16} />
          </span>
        )}
      </div>
      {loading
        ? <div className="skeleton h-9 w-28 mt-4" />
        : <p className={clsx('text-[32px] font-semibold tabular mt-4 leading-none tracking-[-0.02em]', accent)}>{value}</p>}
      {sub && <p className="mt-2 text-[13px] leading-snug text-ink-500">{sub}</p>}
    </button>
  );
}

/** Two-column definition list used across detail panes. */
export function DetailList({
  items, className, columns = 2,
}: {
  items: { label: string; value: React.ReactNode; full?: boolean }[];
  className?: string; columns?: number;
}) {
  return (
    <dl className={clsx('grid gap-x-6 gap-y-3', columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3', className)}>
      {items.filter((i) => i.value !== undefined && i.value !== null && i.value !== '').map((item, i) => (
        <div key={i} className={item.full ? 'sm:col-span-full' : undefined}>
          <dt className="text-[14px] text-ink-500 font-medium">{item.label}</dt>
          <dd className="text-[15px] text-ink-800 dark:text-ink-200 mt-0.5 break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
